import { getSogniClientInstance, APP_SOURCE } from '../sogni-client.svelte.js';
import { canUseStripePortal } from './subscription-ui.js';
import {
  fetchPlanContent,
  createSubscriptionCheckout,
  requestSubscriptionPlanChange,
  activateStripeTrialPaid
} from './subscription-api.js';

/**
 * Billing service — Stripe spark-pack purchases and Unlimited subscriptions.
 *
 * All flows are owned by the Sogni backend (api.sogni.ai): the client only
 * creates the Checkout session and polls status. Stripe Checkout opens in a new
 * tab; balance/subscription updates arrive over the SDK socket, so no client
 * webhook is required. Mirrors sogni-web / sogni-chat.
 */

// Post-checkout redirect target the API uses to build the Stripe return URL.
// Allowed by the API: web | app | dashboard | photobooth | chat.
const REDIRECT_TYPE = import.meta.env.VITE_SOGNI_REDIRECT_TYPE || 'web';

function requireClient() {
  const client = getSogniClientInstance();
  if (!client) throw new Error('Sogni client not ready');
  return client;
}

function unwrap(res, key) {
  const data = res?.data ?? res;
  if (key && data && data[key] !== undefined) return data[key];
  return data;
}

/* ------------------------------------------------------------------ */
/* Spark packs (one-time token purchases)                              */
/* ------------------------------------------------------------------ */

/**
 * List available spark packs.
 * @returns {Promise<Array<{ id, priceUsd, sparkValue, label, raw }>>}
 */
export async function listSparkProducts() {
  const client = requireClient();
  const res = await client.apiClient.rest.get('/v1/iap/stripe/products');
  const products = unwrap(res, 'products') || [];
  return (Array.isArray(products) ? products : [])
    .map((p) => {
      const unitAmount = Number(p.unit_amount ?? p.unitAmount ?? 0);
      const sparkValue = Number(p.metadata?.sparkValue ?? p.sparkValue ?? 0);
      return {
        id: p.product ?? p.id ?? p.productId,
        priceUsd: unitAmount ? unitAmount / 100 : Number(p.priceUsd ?? 0),
        sparkValue,
        label: p.name ?? p.label ?? `${sparkValue} Spark`,
        raw: p
      };
    })
    .filter((p) => p.id)
    .sort((a, b) => a.priceUsd - b.priceUsd);
}

/**
 * Start a spark-pack purchase. Returns the Stripe Checkout URL + purchase id.
 * @param {string} productId
 * @returns {Promise<{ url: string, purchaseId: string, message?: string }>}
 */
export async function startSparkPurchase(productId) {
  const client = requireClient();
  const res = await client.apiClient.rest.post('/v1/iap/stripe/purchase', {
    productId,
    redirectType: REDIRECT_TYPE,
    appSource: APP_SOURCE
  });
  const data = unwrap(res);
  return { url: data?.url, purchaseId: data?.purchaseId, message: data?.message };
}

/**
 * Poll a purchase's status.
 * @param {string} purchaseId
 * @returns {Promise<'initiated'|'processing'|'completed'|'expired'|string>}
 */
export async function getPurchaseStatus(purchaseId) {
  const client = requireClient();
  const res = await client.apiClient.rest.get(`/v1/iap/status/${purchaseId}`);
  const data = unwrap(res);
  return data?.status ?? data;
}

/* ------------------------------------------------------------------ */
/* Subscriptions (Unlimited)                                           */
/* ------------------------------------------------------------------ */

export async function fetchSubscriptionStatus() {
  const client = requireClient();
  return client.account.refreshSubscription();
}

export async function fetchSubscriptionPlans() {
  const client = requireClient();
  return client.account.getSubscriptionPlans();
}

export async function fetchSubscriptionPlanContent() {
  return fetchPlanContent(requireClient());
}

export async function fetchTrialEligibility() {
  const client = requireClient();
  return client.account.getTrialEligibility();
}

/**
 * Create a subscription checkout session.
 * @returns {Promise<string|null>} Stripe Checkout URL
 */
export async function startSubscriptionCheckout(planId, term, options = {}) {
  const client = requireClient();
  const result = await createSubscriptionCheckout(client, planId, term, {
    redirectType: REDIRECT_TYPE,
    appSource: APP_SOURCE
  }, options);
  if (!result?.url) throw new Error('Checkout did not return a payment link. Please try again.');
  return result.url;
}

export async function changeSubscriptionPlan(planId, term, billingConsent) {
  return requestSubscriptionPlanChange(requireClient(), planId, term, {
    redirectType: REDIRECT_TYPE,
    appSource: APP_SOURCE
  }, billingConsent);
}

export async function startPaidTrialBilling(billingConsent) {
  return activateStripeTrialPaid(requireClient(), billingConsent);
}

/**
 * Open the Stripe billing portal (manage / cancel). The provider check lives
 * here as a second line of defense so Apple, Google, manual, and unknown
 * subscriptions can never accidentally create a Stripe portal session.
 * @param {{ provider?: string } | null} subscription
 * @returns {Promise<string|null>} portal URL
 */
export async function getSubscriptionPortalUrl(subscription) {
  if (!canUseStripePortal(subscription)) return null;
  const client = requireClient();
  const result = await client.account.createSubscriptionPortalSession();
  return result?.url ?? null;
}
